import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'

/**
 * Dialog component for choosing whether to edit only this occurrence
 * or all future occurrences of a recurrent activity
 */
function RecurrentEditDialog({
  open,
  onClose,
  onConfirm,
  activityName,
  loading = false,
  error = null,
  timingEdited = false
}) {
  const [editScope, setEditScope] = React.useState('this')

  React.useEffect(() => {
    if (open) {
      setEditScope('this')
    }
  }, [open])

  const handleConfirm = () => {
    onConfirm(editScope)
  }

  const handleScopeChange = (event) => {
    setEditScope(event.target.value)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Muokkaa toistuvaa tapahtumaa</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Tämä on toistuva tapahtuma. Miten haluat tallentaa muutokset?
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {activityName}
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            value={editScope}
            onChange={handleScopeChange}
          >
            <FormControlLabel
              value="this"
              control={<Radio />}
              label={
                <div>
                  <Typography variant="body1">Vain tätä tapahtumaa</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Muutokset koskevat vain tätä yksittäistä tapahtumaa.
                    Tämä tapahtuma ei enää päivity automaattisesti.
                  </Typography>
                </div>
              }
            />
            <FormControlLabel
              value="future"
              control={<Radio />}
              label={
                <div>
                  <Typography variant="body1">Tätä ja kaikkia tulevia</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Muutokset koskevat tätä tapahtumaa ja kaikkia tulevia tapahtumia sarjassa.
                  </Typography>
                  {timingEdited ? (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      Jokainen kerta pitää oman päivänsä; kellonaika, ilmoittautuminen ja peruutuksen takaraja
                      siirtyvät samoin. Päivän siirto onnistuu vain yksittäiselle tapahtumalle.
                    </Typography>
                  ) : null}
                </div>
              }
            />
          </RadioGroup>
        </FormControl>
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Peruuta
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Tallennetaan...' : 'Tallenna'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RecurrentEditDialog
